import { createSignal } from '@react-rxjs/utils'
import _ from 'lodash'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  race,
  switchMap,
  take,
  timer,
} from 'rxjs'

import { bufferUntil, takeOnePerKey } from '../../utils/rx/operators'
import {
  latestConsulted$,
  latestCustomerEmailHash$,
  latestFirstTimePurchase$,
  latestUserCategory$,
} from '../auth/state'
import { parseDispensaryPathParts } from '../dispensaries'
import { latestMagentoUserGroup$ } from '../m2/user-groups'
import { AnalyticsEvent } from './events'
import { AnalyticsEventMyPurchasesReorderSearch } from './events/account-purchases'
import { AnalyticsEventMyRewardsInvoiceSearch } from './events/account-rewards'
import {
  AnalyticsEventAccountOnboardingTourComplete,
  AnalyticsEventAccountOnboardingTourInterrupted,
} from './events/account-tour'
import { AnalyticsEventCopyEScript, AnalyticsEventStartEscriptBuilder } from './events/escript'
import { AnalyticsEventExpressCheckoutProp65Review } from './events/express-checkout'
import { AnalyticsEventInteractionCheckerSearch } from './events/interaction-checker'
import {
  AnalyticsEventBeginCheckout,
  AnalyticsEventPageView,
  AnalyticsEventSearch,
  AnalyticsEventSelectContent,
  AnalyticsEventViewCart,
  AnalyticsEventViewItem,
} from './events/recommended'
import { AnalyticsEventSearchNoResults } from './events/search'

export const [analyticsEventChange$, setAnalyticsEvent] = createSignal<AnalyticsEvent>()

/** Behavior subject to track when a (Gatsby) route update has occurred */
export const onRouteUpdateTick$ = new BehaviorSubject<void>(void 0)

/**
 * Truncate parameter values to their maximum length to avoid discarded events
 * Ref: https://support.google.com/analytics/answer/9267744?hl=en
 */
const truncateParameterValuesAsNeeded = <T>(event: T): T => {
  if (typeof event !== 'object' || event == null) return event
  const updatedEvent: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(event)) {
    if (typeof value === 'string' && value.length > 100) {
      updatedEvent[key] = value.slice(0, 100)
    } else {
      updatedEvent[key] = value
    }
  }
  return updatedEvent as T
}

const onboardingTourEvent$ = analyticsEventChange$.pipe(
  filter(
    (analyticsEvent) =>
      analyticsEvent.event === 'account_onboarding_tour_complete' ||
      analyticsEvent.event === 'account_onboarding_tour_interrupted',
  ),
  // Deduplicate by tour name and event type
  takeOnePerKey((event) => `${event.event}_${event.tour_title}`, 'TAKE_IF_KEY_EMPTY'),
)

const pageViewEvent$ = analyticsEventChange$.pipe(
  filter(
    (analyticsEvent): analyticsEvent is AnalyticsEventPageView =>
      analyticsEvent.event === 'page_view',
  ),
  distinctUntilChanged(
    ({ page_path: prevPagePath }, { page_path: nextPagePath }) => prevPagePath === nextPagePath,
  ),
  map((analyticsEvent) => {
    const parts = parseDispensaryPathParts(analyticsEvent.page_path || '')
    return {
      ...analyticsEvent,
      dispensary_path_prefix: parts ? `${parts.prefix}${parts.slug}` : undefined,
      normalized_page_path: parts ? parts.restOfPath || '/' : analyticsEvent.page_path,
    }
  }),
)

const searchEvent$ = analyticsEventChange$.pipe(
  filter(
    (
      analyticsEvent,
    ): analyticsEvent is
      | AnalyticsEventInteractionCheckerSearch
      | AnalyticsEventMyPurchasesReorderSearch
      | AnalyticsEventMyRewardsInvoiceSearch
      | AnalyticsEventSearch
      | AnalyticsEventSearchNoResults =>
      analyticsEvent.event === 'interaction_checker_search' ||
      analyticsEvent.event === 'my_purchases_reorder_search' ||
      analyticsEvent.event === 'my_rewards_invoice_search' ||
      analyticsEvent.event === 'search' ||
      analyticsEvent.event === 'search_no_results',
  ),
  debounceTime(3000),
)

const selectContentEvent$ = onRouteUpdateTick$.pipe(
  switchMap(() =>
    analyticsEventChange$.pipe(
      filter(
        (analyticsEvent): analyticsEvent is AnalyticsEventSelectContent =>
          analyticsEvent.event === 'select_content',
      ),
      takeOnePerKey((event) => event._key, 'TAKE_IF_KEY_EMPTY'),
      // exclude _key from event
      map(({ _key, ...event }) => event),
    ),
  ),
)

// Send only one `begin_checkout` per cart id, regardless of how many checkout steps fire it.
// Dedup state lives at module scope because `nonPageViewEvent$` is resubscribed on every route
// update, which would reset any per-subscription operator state (e.g. `takeOnePerKey`)
const sentBeginCheckoutKeys = new Set<string>()
const beginCheckoutEvent$ = analyticsEventChange$.pipe(
  filter(
    (analyticsEvent): analyticsEvent is AnalyticsEventBeginCheckout =>
      analyticsEvent.event === 'begin_checkout',
  ),
  filter(({ _key }) => {
    if (!_key) {
      return true
    }
    if (sentBeginCheckoutKeys.has(_key)) {
      return false
    }
    sentBeginCheckoutKeys.add(_key)
    return true
  }),
  // exclude _key from event
  map(({ _key, ...event }) => event),
)

// Best effort: one `copy_escript` per link per page visit. `nonPageViewEvent$` is resubscribed on
// every route update, which resets the operator state, and also whenever the auth state changes,
// so a token refresh can let a repeat copy through. `selectContentEvent$` has the same weakness
const copyEScriptEvent$ = analyticsEventChange$.pipe(
  filter(
    (analyticsEvent): analyticsEvent is AnalyticsEventCopyEScript =>
      analyticsEvent.event === 'copy_escript',
  ),
  takeOnePerKey((event) => event.escript_link, 'TAKE_IF_KEY_EMPTY'),
)

const startEScriptEvent$ = onRouteUpdateTick$.pipe(
  switchMap(() =>
    analyticsEventChange$.pipe(
      filter(
        (analyticsEvent): analyticsEvent is AnalyticsEventStartEscriptBuilder =>
          analyticsEvent.event === 'start_escript',
      ),
      // Take only one start_escript event per page view
      take(1),
    ),
  ),
)

// A sheet can refuse California at each address change and again at authorization; count it once
const expressCheckoutProp65ReviewEvent$ = analyticsEventChange$.pipe(
  filter(
    (analyticsEvent): analyticsEvent is AnalyticsEventExpressCheckoutProp65Review =>
      analyticsEvent.event === 'express_checkout_prop65_review',
  ),
  takeOnePerKey((event) => event._key, 'TAKE_IF_KEY_EMPTY'),
  // exclude _key from event
  map(({ _key, ...event }) => event),
)

const viewEvent$ = analyticsEventChange$.pipe(
  filter(
    (analyticsEvent): analyticsEvent is AnalyticsEventViewCart | AnalyticsEventViewItem =>
      analyticsEvent.event === 'view_cart' || analyticsEvent.event === 'view_item',
  ),
  distinctUntilChanged(_.isEqual),
)

const otherEvent$ = analyticsEventChange$.pipe(
  filter(
    (
      analyticsEvent,
    ): analyticsEvent is Exclude<
      AnalyticsEvent,
      | AnalyticsEventAccountOnboardingTourComplete
      | AnalyticsEventAccountOnboardingTourInterrupted
      | AnalyticsEventBeginCheckout
      | AnalyticsEventCopyEScript
      | AnalyticsEventExpressCheckoutProp65Review
      | AnalyticsEventMyPurchasesReorderSearch
      | AnalyticsEventPageView
      | AnalyticsEventSearch
      | AnalyticsEventSearchNoResults
      | AnalyticsEventStartEscriptBuilder
      | AnalyticsEventViewCart
      | AnalyticsEventViewItem
    > => {
      const { event } = analyticsEvent
      return (
        event !== 'account_onboarding_tour_complete' &&
        event !== 'account_onboarding_tour_interrupted' &&
        event !== 'begin_checkout' &&
        event !== 'copy_escript' &&
        event !== 'express_checkout_prop65_review' &&
        event !== 'interaction_checker_search' &&
        event !== 'my_purchases_reorder_search' &&
        event !== 'my_rewards_invoice_search' &&
        event !== 'page_view' &&
        event !== 'search' &&
        event !== 'search_no_results' &&
        event !== 'select_content' &&
        event !== 'start_escript' &&
        event !== 'view_cart' &&
        event !== 'view_item'
      )
    },
  ),
)

const nonPageViewEvent$ = merge(
  beginCheckoutEvent$,
  copyEScriptEvent$,
  expressCheckoutProp65ReviewEvent$,
  onboardingTourEvent$,
  searchEvent$,
  selectContentEvent$,
  startEScriptEvent$,
  viewEvent$,
  otherEvent$,
)

const mergedAnalyticsEvent$ = merge(
  pageViewEvent$,
  // after each router update, buffer non-page view events until the next page view event or 100ms
  // - this prevents sending events pushing during rendering prior to the page view event (which is
  //   intentionally delayed by ~32ms)
  onRouteUpdateTick$.pipe(
    switchMap(() => nonPageViewEvent$.pipe(bufferUntil(race(pageViewEvent$, timer(100))))),
  ),
)

// get the latest auth0 data for merging into event payload
const analyticsEvent$ = combineLatest([
  latestUserCategory$,
  latestMagentoUserGroup$,
  latestConsulted$,
  latestFirstTimePurchase$,
  latestCustomerEmailHash$,
]).pipe(
  switchMap(([userCategory, magentoUserGroup, consulted, firstTimePurchase, customerEmailHash]) =>
    // subscribe to `mergedAnalyticsEvent$` inside `switchMap` so that each event is handled once
    mergedAnalyticsEvent$.pipe(
      map((eventPayload) => {
        return {
          ...eventPayload,
          ...(userCategory && {
            user_category: userCategory,
          }),
          ...(magentoUserGroup && {
            user_m2_group: magentoUserGroup,
          }),
          // Add consulted and first_time_purchase (default to null if not set)
          consulted,
          first_time_purchase: firstTimePurchase,
          // Add hashed email (requested by Tinuiti)
          customer_hems: customerEmailHash,
        }
      }),
      map(truncateParameterValuesAsNeeded),
    ),
  ),
)

const analyticsUserProperties$ = latestConsulted$.pipe(
  distinctUntilChanged(),
  map((consulted) => ({ consulted })),
)

// create immediate subscription to observable
if (typeof window !== 'undefined') {
  analyticsUserProperties$.subscribe((userProperties) => {
    if (!window.gtag) {
      console.log(`Analytics user_properties:`, JSON.stringify(userProperties))
      return
    }
    window.gtag('set', 'user_properties', userProperties)
  })
  analyticsEvent$.subscribe(({ event, ...payload }) => {
    if (!window.gtag) {
      console.log(`Analytics event [${event}]:`, JSON.stringify(payload))
      return
    }
    window.gtag('event', event, payload)
  })
}
